// ════════════════════════════════════════════════════════════════════════════
// The attach-result BLOCK GRAMMAR — the shape a materialised attach takes in
// content, replacing the JSON dump the sentinel used to print (ruled
// 2026-08-19: "it should be a formatted prose, that will have RELEVANT data
// items within it… a dedicated format that will have the items in a way that
// could be both human readable, but also parsable and reliable").
//
// Modelled on the map fence, which solves the same problem: one fenced block,
// `key: value` directives, one line per addressable thing, tagged fields where
// a value could collide with prose, and defensive ceilings so a hostile block
// cannot become unbounded work. The differences are forced by the subject —
// a map pin is one line, a hotel is a record — so an ITEM here is a headline
// line plus its own indented tagged field lines.
//
// Three parties read a materialised block and the grammar serves all three:
// a person reading the version body, the FE parser rendering a card per item,
// and a model re-reading the content it wrote in an earlier round.
//
// This file is TYPES AND CONSTANTS ONLY. The renderer, the parser and the
// per-pillar projectors consume it; nothing here does work.
// ════════════════════════════════════════════════════════════════════════════

import type { PillarCode } from '../session/orchestration/tool-protocol/follow-up-refs.js';

/** Block-level directive keys. A closed set: a line whose key is not one of
 *  these is not a directive, and the parser treats it as body. */
export enum AttachDirectiveKey {
  /** The PILLAR these results came from, named for the reader (ruled
   *  2026-08-20: "kind: names the pillar"). The value's spelling and the read
   *  back off it are `pillar-names.ts`'s. */
  Kind = 'kind',
  /** What this attach IS, in the reader's words: the search's own framing,
   *  never our plumbing. One line. */
  About = 'about',
}

/** The generic item's field vocabulary — the mechanism's own closed set, the
 *  way each pillar declares its own. Three fields, and each is here because a
 *  reader acts on it: the address the row opens at, the result's own words
 *  about itself, and the picture the search returned for it.
 *
 *  It lives beside the grammar rather than beside the projector that writes it
 *  because BOTH sides need it: the BL writes these keys onto a row nobody has a
 *  format for, and the FE's fallback render reads them back off the parsed
 *  item. One spelling, one home — the same rule the fence and the separators
 *  are kept under. */
export enum AttachLinkFieldKey {
  Link = 'link',
  Description = 'description',
  Photo = 'photo',
}

/** The reserved COMMON item-metadata key: the writer's own `[label](url)`
 *  comment on ONE attached item (ruled 2026-08-25, decision 2 — a one-row
 *  link's label is that card's comment; a whole-search link's label is block
 *  framing and never reaches an item).
 *
 *  Deliberately its OWN constant, outside `AttachLinkFieldKey`: the generic
 *  enum is fallback vocabulary for a row nobody has a format for, while this
 *  key rides PILLAR items too — and the strict authored readers must never
 *  meet it, so stage-one parsing (`parse-attach-document.ts`) lifts it off the
 *  field list into the item's `writerLabel` before any pillar reader runs.
 *
 *  The spelling is censused against every field vocabulary in the system —
 *  the two directive keys, the three generic keys, all twenty-two generated
 *  authored vocabularies (authored-fields.generated.ts) and the three semantic
 *  codecs — and collides with none. It is also a spelling no pillar can EVER
 *  own: it names the block's writer, never the captured record (`note` and
 *  `comment` were rejected exactly because Finance and Social own them as
 *  source data; bare `label` because a music pillar plausibly will). */
export const ATTACH_WRITER_LABEL_FIELD_KEY = 'writer label';

/** One tagged field line inside an item: `  key: value`.
 *
 *  `key` comes from the pillar's own closed vocabulary (each pillar declares
 *  an enum; never loose strings). `value` is single-line text — a newline in
 *  a captured value is folded to a space at render, because a line break
 *  would end the field and the next line would parse as a sibling.
 *
 *  REPEATING a key is how an ordered sub-array is written (three `segment:`
 *  lines are a three-leg itinerary, in order). There are no nested objects:
 *  a sub-record folds onto one line under the divider its own population
 *  proves safe (D18/D21), or takes a field of its own. */
export interface AttachFieldLine {
  readonly key: string;
  readonly value: string;
}

/** One item — one row of the attached search.
 *
 *  `headline` is the human read, and — RULED 2026-08-20 — NOTHING ELSE. It is
 *  a DISPLAY LINE composed FROM the fields below it, so every datum an item
 *  holds lives in an addressable field and the headline states none of its own.
 *  The rule replaced the opposite one: the headline used to carry values that
 *  appeared nowhere below, which is what made an item unparsable —
 *  `TSUKIMI HOTEL | hotel | ⭐⭐⭐ | 📍Gion, JP (35.0009321, 135.7784988) |
 *  4.8/5 498 reviews` folded six data points into one prose string, and a
 *  widget could not get a rating, a star class or a coordinate out of it
 *  without regexing prose per pillar.
 *
 *  Because it is composed rather than authored, the duplication D17 forbade is
 *  not what this is: there is exactly ONE source of truth (the fields), and the
 *  headline is a projection of it. A reader that deletes every headline from a
 *  stored block loses the human scan and no data at all — which is the property
 *  the per-pillar readers in `shapes/` rely on, and they never read it.
 *
 *  There is NO lean/detail marker, deliberately: the field set IS the shape.
 *  A pillar declares a lean vocabulary and a detail vocabulary where detail
 *  ⊇ lean, so a richer card is chosen on fields that are present as fact,
 *  never on a flag the reader cannot act on (D20). */
export interface AttachItemBlock {
  readonly headline: string;
  readonly fields: readonly AttachFieldLine[];
}

/** The window a block's data was captured over — both ends ISO-8601 instants,
 *  `min` the earliest contributing search's capture and `max` the latest. A
 *  single-search block carries the same instant on both ends, and the caption
 *  prints it in the single-instant form it always has; a merged block (one
 *  pillar, several searches — the 2026-08-25 auto-split ruling) prints the
 *  ruled range reading. The caption is a READING of this range, never the
 *  range itself — see `captureRangeLabel`. */
export interface CapturedAtRange {
  readonly min: string;
  readonly max: string;
}

/** A whole materialised block, before it is rendered to text.
 *
 *  `pillar` is what the `kind:` directive prints (ruled 2026-08-20). It is a
 *  FACT of the attached search rather than a verdict on the items: a patents
 *  block says patents whether its items came out in the patents format or
 *  degraded to the generic one, because what a reader — and a card — needs to
 *  know first is what these results ARE. What SHAPE they are in is the field
 *  set, which is the grammar's own answer to that question (see
 *  `AttachItemBlock`: there is no lean/detail marker, and for the same
 *  reason).
 *
 *  NULL is the PLAIN-LINK block (decision 8, ruled 2026-08-22): a block with
 *  no search behind it — links the writer's live store did not answer, pasted
 *  as links, unaugmented. Its `kind:` prints the plain-link family's own name
 *  (`AttachedResultKind.Links`), never a pillar's, because there is no honest
 *  pillar to claim; its capture time is the write's own instant, which is the
 *  one time the block can prove about links it holds only as pasted. */
export interface AttachBlockDocument {
  readonly pillar: PillarCode | null;
  /** One ISO instant, or the capture WINDOW of a merged block. A plain string
   *  is the single-instant contract every pre-merge caller already satisfies;
   *  a `CapturedAtRange` renders as the ruled range caption (2026-08-25), which
   *  collapses byte-identically to the single-instant form when both ends
   *  print the same minute. */
  readonly capturedAt: string | CapturedAtRange;
  readonly about: string | null;
  readonly items: readonly AttachItemBlock[];
}

/**
 * The per-pillar seam. One projector per RowKind the pillar mints.
 *
 * `row` is the list record as the search minted it — the lean payload.
 * `detailPayload` is the payload of the drill this row was opened with, when
 * one was captured, else null: a full search whose rows were drilled attaches
 * those rows in their DETAIL shape and the rest lean, in one block (ruled
 * 2026-08-19).
 *
 * Answering `null` means "this record is not the shape this projector reads" —
 * the block then degrades to the WHOLE generic render rather than to a partial
 * card, exactly as the payload builders already do.
 */
export type AttachItemProjector = (input: {
  readonly row: RowRefRecordLike;
  readonly detailPayload: unknown;
  /** The DURABLE citation link the writer attached this row under — a
   *  DB-persisted `/l/<host>/<code>` short link — or null when the row arrived
   *  through the search's own link and none was pasted per row.
   *
   *  Prefer it over the raw captured url when both exist: it is the address the
   *  reader was shown, it survives the session (the ref store does not, U1),
   *  and it is short where a vendor url can run hundreds of characters. Added
   *  on the travel lane's evidence that a projector reading only the record
   *  could never print the link the SPEC's own worked example shows. */
  readonly citationUrl?: string | null;
  /** The writer's own markdown label for the link (U14), when they gave one. */
  readonly label?: string | null;
}) => AttachItemBlock | null;

/** The record fields a projector may read. Structural on purpose: the
 *  projectors live in BL, the grammar lives here, and this keeps the contract
 *  from dragging the session tool-protocol types into every consumer. */
export interface RowRefRecordLike {
  readonly ref: string;
  readonly kind: string;
  readonly payload: unknown;
  readonly address: {
    /** The row's RAW captured url, where it has one. */
    readonly url?: string | undefined;
    /** Native identifiers the row was minted with — an OL key, a DOI, a
     *  videoId, an `owner/repo` pair. Widened into this contract on the
     *  web-current lane's evidence: a GitHub tree entry cannot name its own
     *  repository and an account row carries no url at all, so a projector
     *  reading only `url` could not render either honestly. */
    readonly nativeIds?: Readonly<Record<string, string>> | undefined;
  };
}

// ─── the printed grammar ────────────────────────────────────────────────────

/** Info string of the fence — the persisted block's ratified name (U11 §7.3),
 *  kept verbatim. The 2026-08-19 ruling replaced the block's BODY and deleted
 *  its START/END markers; it did not touch what the block is called, and the
 *  name is load-bearing model-facing copy: a model re-reading its own content
 *  meets `no-edit-data-block` and knows what the block is for before it reads a
 *  byte of it (SPEC §2b).
 *
 *  The block is bounded by this fence, which is why the START/END markers the
 *  first build printed are gone — they marked bounds the fence already carries. */
export const ATTACH_FENCE_INFO_STRING = 'no-edit-data-block';

/** An item opens with a bullet at column 0. Models emit `-`, `*` and `+`
 *  interchangeably in markdown, and a stored block is re-read by models, so
 *  the parser accepts all three and the renderer writes `- `. */
export const ATTACH_ITEM_BULLET = '- ';
export const ATTACH_ITEM_BULLET_REGEX = /^[-*+]\s+(.*)$/;

/** Field lines are indented under their item. Two spaces written; the parser
 *  accepts any run of spaces or tabs, because indentation is the one thing a
 *  copy-paste through another surface is most likely to change. */
export const ATTACH_FIELD_INDENT = '  ';
export const ATTACH_FIELD_LINE_REGEX = /^[ \t]+([A-Za-z][A-Za-z0-9 _-]{0,31}):[ \t]*(.*)$/;

/** A block-level directive: `key: value` at column 0. */
export const ATTACH_DIRECTIVE_REGEX = /^([A-Za-z][A-Za-z0-9_-]{0,31}):[ \t]*(.*)$/;

/** What the renderer writes between a key and its value, on a directive line
 *  and on a field line alike. Beside the two regexes that read it back, so the
 *  written form and the read form cannot drift — the reason `map-constants.ts`
 *  holds the map's separator next to the map's regexes. */
export const ATTACH_FIELD_SEPARATOR = ': ';

/** Defensive ceilings, in the map's spirit: far beyond anything a real search
 *  produces, so they never truncate a genuine attach, and present so that a
 *  malformed or hostile block cannot put unbounded text into a parser or a
 *  React render path. Over-long lines are dropped whole rather than trimmed,
 *  so a drop is visible instead of silently mangling a value. */
export const ATTACH_MAX_ITEMS = 60;
/** RAISED 40 → 120 by the typed re-cut (2026-08-20), and the reason is what
 *  the re-cut changed rather than a ceiling that turned out to pinch. When the
 *  headline carried data, an item's field count was a SUMMARY length and forty
 *  was generous. Now every datum is its own addressable line — a hotels drill
 *  item states fifteen amenities, thirteen review aspects and six offers, each
 *  a line — so the count is the item's DATA SIZE, and a bound set for summaries
 *  would truncate genuine records, which is exactly what these ceilings exist
 *  never to do. It stays a DEFENSIVE bound: the line ceiling and the block
 *  ceiling below are what actually bound the cost, and no captured item in any
 *  pillar comes near 120. */
export const ATTACH_MAX_FIELDS_PER_ITEM = 120;
export const ATTACH_MAX_LINE_LENGTH = 600;
export const ATTACH_MAX_BLOCK_LENGTH = 120_000;
